import { DOCUMENT, isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, REQUEST, inject, isDevMode } from '@angular/core';

import { Content } from '@23degrees/shared-code/models';

import { convertQuillToString } from '@23degrees/shared-code/content/content-utilities';
import { Meta, Title } from '@angular/platform-browser';
import { JsonLDService } from '../jsondld/jsonld.service';

@Injectable({ providedIn: 'root' })
export class GlobalService {
  private request = inject(REQUEST, { optional: true });
  private platformId = inject(PLATFORM_ID);
  document = inject<Document>(DOCUMENT);
  private meta = inject(Meta);
  private title = inject(Title);
  private jsonLdService = inject(JsonLDService);

  private contentStore: Record<string, Content> = {};

  baseUrl(): string {
    let baseUrl: string;
    if (this.isServer()) {
      // console.log('HOST', this.request.hostname, this.request.protocol)
      // if (import.meta.env.DEV) return import.meta.env.BASE_URL;
      // console.log(this.request);

      const protokoll = isDevMode() ? 'http://' : 'https://'; // this.request.protocol;
      const host = isDevMode() ? 'localhost:2385' : (this.request?.headers.get('host') ?? 'localhost:2385'); //'172.18.0.11:8080'; // this.request.get('x-forwarded-host') ||
      baseUrl = protokoll + host;
    } else {
      const url = this.document.URL;
      baseUrl = url.substr(0, url.indexOf('/', url.indexOf('//') + 2));
    }

    return baseUrl;
  }

  private isEditorChildFix = (str, isEditor = false) => {
    if (typeof str === 'string' && str.length > 0) {
      if (isEditor && str.substring(0, 5) === 'edit-') return str;
      if (str.substring(0, 5) !== 'edit-') return str;
    }

    return undefined;
  };

  /** This seems to be a very odd way to get root params */
  public getParameters = (url?: string) => {
    url = url ?? this.document.location.href;
    const regex =
      /(http:\/\/(?:localhost:2385)?|https:\/\/(?:(?:doh|app).23degrees.(?:eu|io)))?\/(embed|viewSub|view|editSub|edit|exportSub|export|dataSub|data)\/([\w-~]+)\/?([\w-~]*)\/?([\w-]*)/;
    const match = url.match(regex);
    if (!match) return {};

    const childSlug = this.isEditorChildFix(match[4]);
    const editor = !childSlug ? this.isEditorChildFix(match[4], true) : this.isEditorChildFix(match[5], true);

    let branch: string;
    let filter: string;
    if (url.includes('?')) {
      const searchParams = new URLSearchParams(url.substring(url.indexOf('?') + 1));
      branch = searchParams.get('branch');
      filter = searchParams.get('filter');

      if (branch) {
        branch = branch.split('#')[0];
      }

      if (filter) {
        filter = filter.split('#')[0];
      }
    }

    return {
      host: match[1],
      mode: match[2],
      slug: match[3],
      childSlug,
      editor,
      branch,
      filter,
    };
  };

  public getSearchParameters = (url?: string) => {
    url = url ?? this.document.location.href;
    if (url.includes('?')) {
      return new URLSearchParams(url.substring(url.indexOf('?') + 1));
    }
  };

  public isApp = () => {
    return (
      this.document.location.href.includes('app.23degrees') ||
      // this.document.location.href.includes('pre.23degrees') ||
      this.document.location.href.includes('future.23degrees')
    ); // environment.target === 'app';
  };

  public isDoh = () => {
    return (
      this.document.location.href.includes('doh.23degrees') ||
      this.document.location.href.includes('pre.23degrees') ||
      this.document.location.href.includes('next.23degrees')
    ); // environment.target === 'doh';
  };

  public isLocalhost = () => {
    return isDevMode() || this.document.location.href.includes('localhost');
  };

  public isEU = () => {
    return this.document.location.href.includes('23degrees.eu');
  };

  public isEmbed = () => {
    return this.document.location.href.includes('/embed/') || this.document.location.href.includes('/crawler/');
  };

  public getCDNLink = (): string => {
    let cdnSubDomain = 'g-cdn';
    if (!this.isApp()) {
      cdnSubDomain = 'g-cdn-doh';
    }

    let cdnTLD = 'io';
    if (this.isEU()) {
      cdnTLD = 'eu';
    }

    const cdnLink = `https://${cdnSubDomain}.23degrees.${cdnTLD}`;
    return cdnLink;
  };

  public getBucketUrl = (): string => {
    let baseUrl = 'https://files.23degrees.io';

    if (this.isEU()) {
      baseUrl = 'https://files.23degrees.eu';
    }

    // if (this.isLocalhost()) {
    //   return '/api/v2/files/get';
    // }

    if (this.isLocalhost() || this.isDoh()) {
      return `${baseUrl}/dev`;
    }

    if (this.isApp()) {
      return `${baseUrl}/files`;
    }

    return `${baseUrl}/files`;
  };

  public getMediaLink = (): string => {
    if (this.isEU()) {
      return 'https://media.23degrees.eu';
    }

    if (this.isDoh()) {
      if (this.isEU()) {
        return 'https://media-doh.23degrees.eu';
      }

      return 'https://media-doh.23degrees.io';
    }

    if (this.isLocalhost()) {
      return 'https://media-doh.23degrees.io';
      // return '/api/v3/content/media/view';
    }

    return 'https://media.23degrees.io';
  };

  public escapeMediaLink = (link: string): string => {
    if (!link) return '';

    // Flags
    if (link.startsWith('flag:')) return this.resolveFlagPath(link);

    // Marker Icons
    if (link.startsWith('icon:')) return this.resolveIconPath(link);

    // Media Images
    if (link.startsWith('image:')) return this.resolveImagePath(link);

    // Media
    if (link.startsWith('media:')) return this.resolveMediaPath(link);

    // ISO fields
    if (link.startsWith('iso:')) return this.resolveISOpath(link);

    // external links
    if (link.startsWith('https://') || link.startsWith('http://')) return link; // Safety net

    // assets
    if (link.startsWith('/assets/') || link.startsWith('/public/') || link.startsWith('data:')) return link;

    // v1 images
    if (link.length === 24 && /[\w]+/.test(link)) return `/services/public/content/imagebyid/${link}`;

    // v2 images
    if (!link.includes('.')) return `${this.getBucketUrl()}/${link}`;

    // v3 media
    return `${this.getMediaLink()}/${link}`;
  };

  // Base SVG Path (This could also point to a S3 bucket)
  private getSVGBasePath = () => '/assets/icons';
  private notFound = `${this.getSVGBasePath()}/notfound.svg`;

  private resolveIconPath = (value: string): string => {
    const icon = value.split('icon:');
    return icon[1] ? `${this.getSVGBasePath()}/marker/${icon[1]}.svg` : this.notFound;
  };

  private resolveImagePath = (value: string): string => {
    const url = value.split('image:');
    return url[1] ? `${this.getMediaLink()}/${url[1]}` : this.notFound;
  };

  private resolveMediaPath = (value: string): string => {
    const url = value.split('media:');
    return url[1] ? `${this.getMediaLink()}/${url[1]}` : this.notFound;
  };

  private resolveFlagPath = (value: string): string => {
    const flag = value.split(':');
    if (flag.length > 2) {
      // Other ISO geographies
      return `${this.getSVGBasePath()}/flags/${flag[1]}/${flag[2]}.svg`;
    }

    // World countries
    return flag[1] ? `${this.getSVGBasePath()}/flags/4x3/${flag[1]}.svg` : this.notFound;
  };

  public worldIsoMappings = [
    'choro_eu_nuts0_202401',
    'choro_world_admin0_states_worldbank5_202509',
    'choro_world_admin0_territory_worldbank5_202509',
    'choro_world_countries_eurostat_202401',
    'choro_world_countries_hkg_202201',
    'choro_world_metropolitan_tagesschau_202504',
    'choro_world_tagesschau_202504',
    'fes_b9_2024',
    'fes_e3_2024',
    'fes_eu27_2023',
    'fes_eu27_en_2024',
    'fes_nato_all_2023',
    'fes_nato_all_2024',
    'fes_nato_all_rublr_2024',
    'fes_nato_small_2023',
    'fes_nato_small_2024',
    'fes_nato_small_rublr_2024',
    'fes_weimarerertriangle_2024',
    'ifw_world_countries_disputed_regions',
    'world_countries_de_2022',
    'world_countries_de',
    'world_countries_disputed_de_2022',
    'world_countries_disputed_en_2022',
    'world_countries_disputed_en',
    'world_countries_disputed_subdivisions_2021',
    'world_countries_disputed_wgs84',
    'world_countries_disputed',
    'world_countries_en_2022',
    'world_countries_grale_de_2023',
    'world_countries_grale_en_2023',
    'world_countries_tagesschau',
    'world_countries',
  ];
  public germanyIsoMappings = ['de_laender_2024'];
  public austriaIsoMappings = ['at_bundeslaender'];

  private resolveISOpath = (value: string) => {
    const parts = value.split(':');
    if (!parts[1] || !parts[2]) return this.notFound;

    const isoMappingId = parts[1];
    const isoCode = parts[2];

    // Iso Codes
    const code = isoCode.split('G_');
    if (!code[1]) return this.notFound;

    if (this.worldIsoMappings.includes(isoMappingId)) return this.resolveFlagPath(`flag:${code[1].toLowerCase()}`);
    if (this.germanyIsoMappings.includes(isoMappingId)) return this.resolveFlagPath(`flag:germany:${code[1].toLowerCase()}`);
    if (this.austriaIsoMappings.includes(isoMappingId)) return this.resolveFlagPath(`flag:austria:${code[1].toLowerCase()}`);

    return this.notFound;
  };

  isServer(): boolean {
    return isPlatformServer(this.platformId);
  }

  isBrowser(): boolean {
    return !isPlatformServer(this.platformId);
  }

  updateMetaInfo(content: Content, embedPage = false) {
    let description: string;

    if (content.description) {
      description = convertQuillToString(content.description).replace(/(\r\n|\n|\r)/gm, '');
    } else {
      description = convertQuillToString(content.name).replace(/(\r\n|\n|\r)/gm, '');
      if (description && content?.typeSpecific?.data?.publisher?.name) {
        const publisherName = convertQuillToString(content.typeSpecific.data.publisher.name).replace(/(\r\n|\n|\r)/gm, '');
        if (publisherName !== 'Quelle: ') {
          description = `${description.trim()}, ${publisherName}`;
        }
      }
    }

    const img = `${this.baseUrl()}/services/image/v1/getPreviewFromSlug/${content.slug}/preview.png`;

    this.setMetaTags(content.fileName, description, img);

    const metaCanonical = this.document.querySelector("link[rel='canonical']");
    const metaRobots = this.document.querySelector("meta[name='robots']");

    if (embedPage) {
      // Add/update canonical
      const link = metaCanonical || this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', `${this.baseUrl()}/view/${content.slug}`);

      if (!metaCanonical) {
        this.document.head.appendChild(link);
      }

      // Add/update robots meta
      this.meta.updateTag({
        name: 'robots',
        content: 'noindex, follow',
      });
    } else {
      // Remove canonical link if present
      if (metaCanonical) {
        this.document.head.removeChild(metaCanonical);
      }

      // Remove robots meta if present
      if (metaRobots) {
        this.document.head.removeChild(metaRobots);
      }
    }
  }

  setMetaTags(name: string, description?: string, imageUrl?: string) {
    this.title.setTitle(name || '23degrees.io - interactive data visualizations');

    this.meta.updateTag({ property: 'og:title', content: name });
    this.meta.updateTag({ name: 'twitter:title', content: name });
    this.meta.updateTag({ name: 'twitter:site', content: '@23degrees_io' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });

    if (description) {
      if (description.length > 160) {
        const lastSpace = description.indexOf(' ', 150);
        description = `${description.substring(0, lastSpace > -1 ? lastSpace : 155)}...`;
      }

      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({ property: 'og:description', content: description });
      this.meta.updateTag({ name: 'twitter:description', content: description });
    } else {
      const altDesc = 'Create, reuse and share interactive, responsive & beautiful data visualizations with 23° Atlas — no coding skills required.';
      this.meta.updateTag({ name: 'description', content: altDesc });
      this.meta.updateTag({ property: 'og:description', content: altDesc });
      this.meta.updateTag({ name: 'twitter:description', content: altDesc });
    }

    if (imageUrl) {
      this.meta.updateTag({ property: 'og:image', content: imageUrl });
      this.meta.updateTag({ name: 'twitter:image', content: imageUrl.replace('preview.png', 'twitter.png') });
      this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    } else {
      const altImage = `${this.baseUrl()}/assets/android-chrome-512x512.png`;
      this.meta.updateTag({ property: 'og:image', content: altImage });
      this.meta.updateTag({ name: 'twitter:image', content: altImage });
      this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    }
  }

  scrollTo = (id: string | HTMLElement, extraSpace: number = 0) => {
    // if (this.isBrowser()) {
    //   // this.router.navigate(['/tags', ...this.currentSlugs], {fragment: tagSlug, replaceUrl: false});
    //   let element;
    //   if (typeof id === 'string') {
    //     element = this.document.getElementById(id);
    //   } else if (id instanceof HTMLElement) {
    //     element = id;
    //   }
    //   if (!element) return;
    //   const page = this.document.getElementsByClassName('content-wrapper')[0];
    //   // if (tagStream) tagStream.scrollIntoView({ block: 'start', behavior: 'smooth' });
    //   if (element) {
    //     const offset = this.bpo.isMatched('(max-width: 900px)') ? 50 : 56;
    //     const pageTop = page.scrollTop;
    //     const scrollTop = element.getBoundingClientRect().top + pageTop - (offset + extraSpace);
    //     if (page.scroll) {
    //       page.scroll({
    //         top: scrollTop,
    //         left: 0,
    //         behavior: 'smooth',
    //       });
    //     } else {
    //       page.scrollTop = scrollTop;
    //     }
    //   }
    // }
  };

  public setContentStore = (refStore: Record<string, Content>) => {
    this.contentStore = refStore;
  };

  public getContentStore = (): Record<string, Content> => this.contentStore;

  setupJsonLd = (content: Content, url: string) => {
    const headline = content.fileName || content.name;
    const baseUrL = this.baseUrl();
    const currentUrl = baseUrL + url;
    const imageLinks = [`${baseUrL}/services/image/v1/getPreviewFromSlug/${content.slug}.png`];

    /**
     * TODO: SSR never seem to provide vDataSetUserInfo so jsonLd is never rendered = makes no sense
     *  if (content && content.vDataSetUserInfo) {
     *
     */

    if (content?.vUserInfo) {
      const schema = this.jsonLdService.tagLandingPageSchema(currentUrl, headline, imageLinks, content.created_at, content.updated_at, content.vUserInfo.name);
      this.jsonLdService.updateScript(schema);
    }
  };

  removeJsonLd() {
    this.jsonLdService.removeScript();
  }
}
